import { IonCol, IonGrid, IonImg, IonRow } from '@ionic/react';
import React, { useState, useEffect } from 'react';
import { baseUrl, cacheBust } from '../helpers/configHelper';
import * as currencyHelpers from '../helpers/currencyHelpers';
import AllergenInfoModal from '../modals/AllergenInfoModal';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import { inlineBrandImage, downloadAllergenFile } from '../helpers/menuHelpers';

interface IHeaderDescription {
    menuItem: IMopMenuItemDetail;
    headerImage: string | null;
    hideText: boolean;
}

const HeaderDescription: React.FC<IHeaderDescription> = ({
    menuItem,
    headerImage,
    hideText,
}) => {
    const headerState = useSelector((state: ApplicationState) => state.headerFooter);
    const [allergenInfoOpen, setAllergenInfoOpen] = useState(false);

    let priceValue = (menuItem && (menuItem.menuItemBasePrice ?? 0) > 0) ? menuItem.menuItemBasePrice:null;
    if (!priceValue && menuItem.menuItemFromPrice) priceValue = menuItem.menuItemFromPrice;
    let price = currencyHelpers.formatCurrency(priceValue);

    function getMenuItemName() {
        return inlineBrandImage(menuItem.menuItemName, menuItem.hasBrandImage);
    }

    // NOTE: This is used to display progress for menu items where you pick up
    // to X, such as Build Your Breakfast. - 2024-05-07
    const modifierGroup = menuItem.modifierGroups.find((modifierGroup: IMopModifierGroup) => (
        modifierGroup.maxDifferentItems > 0
    ));

    const maxDifferentItems = modifierGroup?.maxDifferentItems;
    const modifierGroupItems = modifierGroup?.modifierGroupItems;

    const [totalQuantitySelected, setTotalQuantitySelected] = useState(0);

    useEffect(() => {
        const total = modifierGroupItems?.reduce((total: number, currentValue: IMopModifierGroupItem) => (
            total += currentValue.quantitySelected
        ), 0);

        setTotalQuantitySelected(total);
    }, [modifierGroupItems]);

    return (
        <div className="header-description-wrapper">
            <IonGrid>
                <IonRow className="image-row">
                    { headerState?.showDebugLabel &&
                        <div className="miDebugLabelHeader">
                            {menuItem?.sku} img:{headerImage}
                        </div>
                    }
                    {!hideText && headerImage &&
                        <IonImg
                            className="product-image"
                            src={headerImage + `?v=${cacheBust()}`}
                        />
                    }
                    {menuItem?.productFlags?.includes("N") &&
                        <IonImg
                            className="new-product-icon"
                            src={`${baseUrl()}/images/assets/new_icon.png`}
                        />
                    }
                </IonRow>
                { !hideText &&
                    <IonRow className="text-row">
                        <IonCol>
                            <h1 className="product-title">
                                <span className="align-children" dangerouslySetInnerHTML={{__html: getMenuItemName()}}></span>
                                {price &&
                                    <span className="base-price">
                                        {currencyHelpers.hasPrefixSymbol(price)
                                            ?
                                            <>
                                                <span className="sign">{price[0]}</span>
                                                {price.substring(1)}
                                            </>
                                            :
                                            price
                                        }
                                    </span>
                                }
                            </h1>
                            {maxDifferentItems && totalQuantitySelected > 0 ?
                                <p className='quantity-selected' color='primary'>
                                    Added <strong>{totalQuantitySelected}</strong> of <strong>{maxDifferentItems}</strong> items
                                </p>
                                :
                                <p dangerouslySetInnerHTML={{__html: inlineBrandImage(menuItem.description, true)}}/>
                            }
                            <div>
                                { menuItem && (menuItem.kcal ?? 0) > 0 &&
                                    <div className='product-kcal'>{menuItem.kcal}kcal</div>
                                }
                                { menuItem && (menuItem.allergenInfo ?? "").length > 0 &&
                                    <p
                                        className="allergen-info-link"
                                        onClick={() => { downloadAllergenFile() }}
                                    >
                                        View Allergens
                                    </p>
                                }
                            </div>
                            { menuItem && menuItem.productFlags.length > 0 &&
                                <div className="product-tags">
                                    {menuItem.productFlags.map((v) => {
                                        return <IonImg key={v} className="product-icon" src={`${baseUrl()}/images/assets/${v}.png`} />
                                    })}
                                </div>
                            }
                        </IonCol>
                    </IonRow>
                }
            </IonGrid>
            <AllergenInfoModal isModalOpen={allergenInfoOpen} closeModalClick={ () => { setAllergenInfoOpen(false) }} allergenHtml={menuItem?.allergenInfo}></AllergenInfoModal>
        </div>
    )
}

export default HeaderDescription;
